// src/scene/stages/IntroStage.tsx
/**
 * Intro Stage — brutalist editorial.
 *
 * Vertical accent strip down the left edge (issue-number cue),
 * heavier eyebrow in brand color, oversized display name, solid-fill
 * CTA, always-visible status indicator. Progress smoothing isolated
 * inside its own component so loading ticks don't re-render the header
 * (kills the prior text-disappear flicker).
 */

import { useState, useRef, memo } from 'react'
import { useFrame } from '@react-three/fiber'
import { Container, Text, Svg } from '@react-three/uikit'
import { useControls, folder } from 'leva'
import { StageShell } from '../StageShell'
import { useResponsiveLayout } from '../useResponsiveLayout'
import {
  ui,
  Panel,
  HStack,
  useHoverScale,
} from '../ui'
import { useVoiceState } from '../../hooks/useVoice'

// SVG icon content for uikit Svg component
const ICONS = {
  github: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/></svg>`,
  linkedin: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>`,
  mail: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z"/><path d="M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z"/></svg>`,
}

const ICON_SRCS = {
  github: `data:image/svg+xml,${encodeURIComponent(ICONS.github)}`,
  linkedin: `data:image/svg+xml,${encodeURIComponent(ICONS.linkedin)}`,
  mail: `data:image/svg+xml,${encodeURIComponent(ICONS.mail)}`,
} as const

const SOCIAL_LINKS = [
  { icon: 'github' as const, href: 'https://github.com/sepehrnour', label: 'GitHub' },
  { icon: 'linkedin' as const, href: 'https://linkedin.com/in/sepehr-nour', label: 'LinkedIn' },
  { icon: 'mail' as const, href: 'mailto:sepehrnour@gmail.com', label: 'Email' },
]

// ─── Color tokens used by this panel only ───────────────────────────────
const ACCENT = ui.colors.primary                  // 0x648cdc
const CTA_BG_IDLE = ui.colors.primary             // solid brand fill
const CTA_BG_IDLE_HOVER = 0x7aa0e8                // ~10% lighter blue
const CTA_TEXT_IDLE = 0xffffff
const CTA_BG_ACTIVE = 0x3a1a1a
const CTA_BG_ACTIVE_HOVER = 0x4a2222
const CTA_TEXT_ACTIVE = 0xdc6464
const ACCENT_STRIP_WIDTH = 3

// ============================================================================
// INTRO STAGE
// ============================================================================

const STAGE1_MOBILE_POSITION: [number, number, number] = [0, 0.22, 2]
const STAGE1_TABLET_POSITION: [number, number, number] = [0, 0.22, 2]
const STAGE1_UI_ROTATION: [number, number, number] = [-0.15, 0, 0]

export function IntroStage() {
  const { isTablet } = useResponsiveLayout()

  const defaultPosition = isTablet ? STAGE1_TABLET_POSITION : STAGE1_MOBILE_POSITION

  const {
    uiPosX, uiPosY, uiPosZ,
    uiRotX, uiRotY, uiRotZ,
  } = useControls('Stage 1 UI', {
    Position: folder({
      uiPosX: { value: defaultPosition[0], min: -5, max: 5, step: 0.01 },
      uiPosY: { value: defaultPosition[1], min: -5, max: 5, step: 0.01 },
      uiPosZ: { value: defaultPosition[2], min: -5, max: 5, step: 0.01 },
    }),
    Rotation: folder({
      uiRotX: { value: STAGE1_UI_ROTATION[0], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotY: { value: STAGE1_UI_ROTATION[1], min: -Math.PI, max: Math.PI, step: 0.001 },
      uiRotZ: { value: STAGE1_UI_ROTATION[2], min: -Math.PI, max: Math.PI, step: 0.001 },
    }),
  })

  return (
    <StageShell
      stageId="intro"
      panelWidth={420}
      panelHeight={260}
      scale={0.7}
      panel={<IntroPanel />}
      panelPosition={[uiPosX, uiPosY, uiPosZ]}
      panelRotation={[uiRotX, uiRotY, uiRotZ]}
    />
  )
}

// ============================================================================
// INTRO PANEL
// ============================================================================

function IntroPanel() {
  const { state, startConversation, endConversation, loadModels } = useVoiceState()
  const { isActive: _talkHoverActive, ...talkButtonHover } = useHoverScale(1.04)

  const isConversationActive = state === 'listening' || state === 'thinking' || state === 'speaking'
  const isError = state === 'error'

  // The agent's real load is bursty (0% -> 33% -> 100% jumps) which fights any
  // smoothing we add. Decouple the loading bar from real progress entirely:
  // ProgressButton owns a scripted timeline (0->90% glide, hold at 90% until
  // models actually finish, 90->100% snap, brief celebration hold, swap).
  const modelsReady = state !== 'loading' && state !== 'idle'
  const [introComplete, setIntroComplete] = useState(false)

  return (
    <Panel width={420} height={260} padding={0}>
      <Container flexDirection="row" flexGrow={1}>
        {/* Glyph pre-warm: forces uikit to rasterize every letter we'll
            need in status labels at mount time, so swapping "AWAY" ->
            "AVAILABLE" doesn't leave a blank gap while V/B rasterize. */}
        <GlyphPreWarm />

        {/* ─── Vertical accent strip (full-height brand bar) ─────────── */}
        <Container width={ACCENT_STRIP_WIDTH} backgroundColor={ACCENT} />

        {/* ─── Content column — natural stack, panel sized to fit ───── */}
        <Container
          flexDirection="column"
          flexGrow={1}
          padding={ui.space.xl}
          gap={ui.space.lg}
        >
          <HeaderBlock />

          {isError ? (
            <RetryButton onClick={() => loadModels()} />
          ) : isConversationActive ? (
            <TalkButton
              active
              onClick={() => endConversation()}
              hover={talkButtonHover}
            />
          ) : !introComplete ? (
            <ProgressButton
              modelsReady={modelsReady}
              onComplete={() => setIntroComplete(true)}
            />
          ) : (
            <TalkButton
              active={false}
              onClick={() => startConversation()}
              hover={talkButtonHover}
            />
          )}

          <FooterRow state={state} />
        </Container>
      </Container>
    </Panel>
  )
}

// ============================================================================
// HEADER BLOCK — memoized, never re-renders during loading ticks
// ============================================================================

const HeaderBlock = memo(function HeaderBlock() {
  return (
    <Container flexDirection="column" gap={ui.space.sm}>
      <Text
        fontSize={ui.font.size.xs}
        fontWeight={ui.font.weight.bold}
        color={ACCENT}
        letterSpacing={4}
      >
        ENGINEER
      </Text>

      <Text
        fontSize={ui.font.size['3xl']}
        fontWeight={ui.font.weight.bold}
        color={ui.colors.text.primary}
        letterSpacing={-2}
        lineHeight={ui.font.leading.tight}
      >
        Sepehr
      </Text>

      <Text
        fontSize={ui.font.size.sm}
        fontWeight={ui.font.weight.normal}
        color={ui.colors.text.secondary}
        lineHeight={ui.font.leading.normal}
      >
        Building embodied agents that run anywhere.
      </Text>
    </Container>
  )
})

// ============================================================================
// CTAs — share 48h footprint, full width, radius md
// ============================================================================

const CTA_HEIGHT = 48
const CTA_RADIUS = ui.radius.md

const TalkButton = memo(function TalkButton({
  active,
  onClick,
  hover,
}: {
  active: boolean
  onClick: () => void
  hover: Omit<ReturnType<typeof useHoverScale>, 'isActive'>
}) {
  return (
    <Container
      height={CTA_HEIGHT}
      width="100%"
      borderRadius={CTA_RADIUS}
      backgroundColor={active ? CTA_BG_ACTIVE : CTA_BG_IDLE}
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      hover={{
        backgroundColor: active ? CTA_BG_ACTIVE_HOVER : CTA_BG_IDLE_HOVER,
      }}
      onClick={onClick}
      {...hover}
    >
      <Text
        fontSize={ui.font.size.sm}
        fontWeight={ui.font.weight.bold}
        color={active ? CTA_TEXT_ACTIVE : CTA_TEXT_IDLE}
        letterSpacing={1.5}
      >
        {active ? 'END CONVERSATION' : 'TALK TO ME'}
      </Text>
    </Container>
  )
})

const RetryButton = memo(function RetryButton({ onClick }: { onClick: () => void }) {
  return (
    <Container
      height={CTA_HEIGHT}
      width="100%"
      borderRadius={CTA_RADIUS}
      backgroundColor={CTA_BG_ACTIVE}
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      hover={{ backgroundColor: CTA_BG_ACTIVE_HOVER }}
      onClick={onClick}
    >
      <Text
        fontSize={ui.font.size.sm}
        fontWeight={ui.font.weight.bold}
        color={CTA_TEXT_ACTIVE}
        letterSpacing={1.5}
      >
        RETRY
      </Text>
    </Container>
  )
})

/**
 * ProgressButton — indeterminate sliding bar.
 *
 * No fake percentages, no real bursty signal. A small chip slides
 * smoothly across the track on a fixed cycle; once `modelsReady` flips
 * true, we animate the chip out + fade and call onComplete.
 *
 * The previous scripted-percentage version felt either dishonest
 * (cached load took artificial 2s) or broken (real load jumped stages).
 * Indeterminate is the right pattern when we genuinely don't have
 * smooth progress to show.
 */
const SLIDE_CYCLE_SECONDS = 1.4   // full loop time for the chip
const SLIDE_CHIP_WIDTH_PCT = 28   // chip occupies 28% of the track
const READY_FADE_SECONDS = 0.35   // brief fade once models are ready, then swap

function ProgressButton({
  modelsReady,
  onComplete,
}: {
  modelsReady: boolean
  onComplete: () => void
}) {
  const [leftPct, setLeftPct] = useState(0)
  const elapsedRef = useRef(0)
  const readyAtRef = useRef<number | null>(null)
  const doneRef = useRef(false)

  useFrame((_, delta) => {
    if (doneRef.current) return
    elapsedRef.current += delta

    // Triangle wave: chip slides left-to-right then right-to-left.
    // Range of left edge: 0 .. (100 - chipWidth).
    const phase = (elapsedRef.current % SLIDE_CYCLE_SECONDS) / SLIDE_CYCLE_SECONDS
    const tri = phase < 0.5 ? phase * 2 : (1 - phase) * 2
    setLeftPct(tri * (100 - SLIDE_CHIP_WIDTH_PCT))

    if (modelsReady) {
      if (readyAtRef.current === null) readyAtRef.current = elapsedRef.current
      if (elapsedRef.current - readyAtRef.current >= READY_FADE_SECONDS) {
        doneRef.current = true
        onComplete()
      }
    }
  })

  return (
    <Container
      height={CTA_HEIGHT}
      width="100%"
      borderRadius={CTA_RADIUS}
      backgroundColor={ui.colors.bg.card}
      borderWidth={1}
      borderColor={ui.colors.border.light}
      overflow="hidden"
      alignItems="center"
      justifyContent="center"
    >
      <Container
        positionType="absolute"
        positionLeft={`${leftPct}%`}
        positionTop={0}
        positionBottom={0}
        width={`${SLIDE_CHIP_WIDTH_PCT}%`}
        backgroundColor={ACCENT}
        opacity={0.20}
      />
      <Text
        fontSize={ui.font.size.sm}
        fontWeight={ui.font.weight.bold}
        color={ui.colors.text.primary}
        letterSpacing={1.5}
      >
        LOADING
      </Text>
    </Container>
  )
}

// ============================================================================
// FOOTER ROW — social icons left + always-visible status pill on the right
// ============================================================================

function FooterRow({ state }: { state: string }) {
  return (
    <HStack gap={ui.space.md} justify="space-between" align="center" width="100%">
      <HStack gap={ui.space.sm} align="center">
        {SOCIAL_LINKS.map((link) => (
          <IconButton key={link.icon} icon={link.icon} href={link.href} />
        ))}
      </HStack>
      <StatusPill state={state} />
    </HStack>
  )
}

// ============================================================================
// GLYPH PRE-WARM — invisible text containing every letter used in status labels
// so uikit rasterizes them at mount; prevents AWAY -> AVAILABLE swap from
// leaving a blank gap while V / B glyphs are generated mid-frame.
// ============================================================================

const GlyphPreWarm = memo(function GlyphPreWarm() {
  return (
    <Container
      positionType="absolute"
      positionLeft={-9999}
      positionTop={-9999}
      width={1}
      height={1}
      overflow="hidden"
    >
      <Text
        fontSize={ui.font.size.xs}
        fontWeight={ui.font.weight.semibold}
        letterSpacing={2}
      >
        AVAILABLE AWAY LISTENING THINKING SPEAKING ERROR LOADING
      </Text>
    </Container>
  )
})

// ============================================================================
// STATUS PILL — always visible: AVAILABLE / LISTENING / THINKING / SPEAKING
// ============================================================================

function StatusPill({ state }: { state: string }) {
  const config = (() => {
    switch (state) {
      case 'listening': return { color: ui.colors.success,   label: 'LISTENING' }
      case 'thinking':  return { color: ui.colors.secondary, label: 'THINKING' }
      case 'speaking':  return { color: ui.colors.primary,   label: 'SPEAKING' }
      case 'error':     return { color: 0xdc6464,            label: 'ERROR' }
      case 'loading':
      case 'idle':      return { color: 0xf5a623,            label: 'AWAY' }
      default:          return { color: ui.colors.success,   label: 'AVAILABLE' }
    }
  })()

  return (
    <HStack gap={ui.space.sm} align="center">
      <Container
        width={6}
        height={6}
        borderRadius={ui.radius.full}
        backgroundColor={config.color}
      />
      <Text
        fontSize={ui.font.size.xs}
        fontWeight={ui.font.weight.semibold}
        color={ui.colors.text.tertiary}
        letterSpacing={2}
      >
        {config.label}
      </Text>
    </HStack>
  )
}

// ============================================================================
// ICON BUTTON — restrained rounded squares
// ============================================================================

const IconButton = memo(function IconButton({ icon, href }: { icon: keyof typeof ICONS; href: string }) {
  return (
    <Container
      width={36}
      height={36}
      borderRadius={ui.radius.md}
      backgroundColor={ui.colors.bg.surface}
      borderWidth={1}
      borderColor={ui.colors.border.subtle}
      alignItems="center"
      justifyContent="center"
      cursor="pointer"
      hover={{
        backgroundColor: ui.colors.bg.card,
        borderColor: ACCENT,
      }}
      onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}
    >
      <Svg
        src={ICON_SRCS[icon]}
        width={14}
        height={14}
        color={ui.colors.text.secondary}
      />
    </Container>
  )
})

export default IntroStage
